import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  CustomerClass,
  CustomerCreateInput,
  CustomerDetailDto,
  CustomerDto,
  DashboardDto,
  DeliveryNoteDetailDto,
  DeliveryNoteUpdateInput,
  InvoiceDetailDto,
  InvoiceSummaryDto,
  InvoiceUpdateInput,
  PackingListCreateInput,
  PackingListDetailDto,
  PackingListUpdateInput,
  PiDetailDto,
  PiPaymentInput,
  PiSummaryDto,
  Paginated,
  ProformaInvoiceCreateInput,
  ProformaInvoiceUpdateInput,
  PurchaseOrderCreateInput,
  PurchaseOrderDetailDto,
  PurchaseOrderUpdateInput,
  StatementDto,
  ProductCreateInput,
  ProductDetailDto,
  ProductPriceDto,
  ProductSummaryDto,
  ProductUpdateInput,
  QuotationCreateInput,
  QuotationDetailDto,
  QuotationSummaryDto,
  QuotationUpdateInput,
  SetPriceInput,
  SettingsDto,
  SettingsUpdateInput,
  SupplierCreateInput,
  SupplierDetailDto,
  SupplierDto,
  SupplierUpdateInput,
  UserCreateInput,
  UserPasswordResetInput,
  UserUpdateInput,
} from "@hisense/shared";
import { ApiClientError, apiFetch } from "@/lib/api";
import { collectPickerOptions } from "@/lib/pickerOptions";

export function useDashboard() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiFetch<{ data: DashboardDto }>("/dashboard").then((r) => r.data),
  });
}

/** TOÀN BỘ khách khớp bộ lọc, qua mọi trang — cho ô chọn và bộ lọc (`collectPickerOptions`). */
function fetchAllCustomers(
  query: { q?: string; classification?: CustomerClass; followers?: "none"; privacy?: "private"; sort: string },
  signal?: AbortSignal,
): Promise<CustomerDto[]> {
  return collectPickerOptions((page, pageSize) =>
    apiFetch<Paginated<CustomerDto>>("/customers", { query: { ...query, page, pageSize }, signal }),
  );
}

/** `classification` and `sort` are applied SERVER-side. Truyền `pageSize` = một trang của danh sách có phân trang
 *  (trang Khách hàng); bỏ trống = ô chọn / bộ lọc, nhận ĐỦ mọi khách xếp theo tên (`fetchAllCustomers`). */
export function useCustomers(params: {
  q?: string;
  classification?: CustomerClass;
  /** "none" = chỉ khách chưa có người theo đơn. */
  followers?: "none";
  /** "private" = chỉ khách riêng tư (quản trị viên). */
  privacy?: "private";
  sort?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ["customers", params],
    queryFn: async ({ signal }): Promise<Paginated<CustomerDto>> => {
      const filters = { q: params.q, classification: params.classification, followers: params.followers, privacy: params.privacy };
      if (params.pageSize !== undefined) {
        return apiFetch<Paginated<CustomerDto>>("/customers", {
          query: { ...filters, sort: params.sort, page: params.page, pageSize: params.pageSize },
          signal,
        });
      }
      const data = await fetchAllCustomers({ ...filters, sort: params.sort ?? "companyName" }, signal);
      return { data, page: 1, pageSize: data.length, total: data.length };
    },
    placeholderData: (previousData) => previousData,
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ["customer", id],
    // `/customers/new` is the create route — there is nothing to fetch.
    enabled: !!id && id !== "new",
    queryFn: () => apiFetch<{ data: CustomerDetailDto }>(`/customers/${id}`).then((r) => r.data),
  });
}
export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomerCreateInput) =>
      apiFetch<{ data: CustomerDetailDto }>("/customers", { method: "POST", body: input }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["customers"] });
      // The editor navigates straight to /customers/<new id>; seed the detail
      // cache so that render does not re-fetch what the POST just returned.
      qc.setQueryData(["customer", r.data.id], r.data);
    },
  });
}
export function useUpdateCustomer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: import("@hisense/shared").CustomerUpdateInput) => apiFetch<{ data: CustomerDetailDto }>(`/customers/${id}`, { method: "PUT", body: input }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["customers"] }); void qc.invalidateQueries({ queryKey: ["customer", id] }); },
  });
}
export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/customers/${id}`, { method: "DELETE" }),
    // Also drop the detail entry: a delete fired from /customers/:id would
    // otherwise leave a stale record in cache for the back navigation.
    onSuccess: (_r, id) => {
      void qc.invalidateQueries({ queryKey: ["customers"] });
      void qc.invalidateQueries({ queryKey: ["customer", id] });
    },
  });
}

/* ── Companies (our own legal entities — payment-company options) ── */
/** Shape mirrors CompanyOption in apps/api/src/services/companies.ts. */
export interface CompanyOptionDto {
  id: string;
  beneficiary: string;
  isDefault: boolean;
}
export function useCompanies() {
  return useQuery({
    queryKey: ["companies"],
    staleTime: 5 * 60_000, // three rows that change once a year
    queryFn: () => apiFetch<{ data: CompanyOptionDto[] }>("/companies").then((r) => r.data),
  });
}

/* ── Yêu cầu v1.8: bằng chứng đặt hàng trên PI (4.3), yêu cầu sản phẩm / bao bì của khách (2.3) ── */
export function usePiAttachments(piId: string | undefined) {
  return useQuery({
    queryKey: ["pi-attachments", piId],
    enabled: !!piId,
    queryFn: () => apiFetch<{ data: import("@hisense/shared").PiAttachmentDto[] }>(`/proforma-invoices/${piId}/attachments`).then((r) => r.data),
  });
}
export function useCustomerRequirements(customerId: string | undefined) {
  return useQuery({
    queryKey: ["customer-requirements", customerId],
    enabled: !!customerId,
    queryFn: () => apiFetch<{ data: import("@hisense/shared").CustomerRequirementDto[] }>(`/customers/${customerId}/requirements`).then((r) => r.data),
  });
}

/* ── Settings ── */
export function useSettings() {
  return useQuery({ queryKey: ["settings"], queryFn: () => apiFetch<{ data: SettingsDto }>("/settings").then((r) => r.data) });
}
export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: SettingsUpdateInput) => apiFetch<{ data: SettingsDto }>("/settings", { method: "PUT", body }), onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["settings"] }),qc.invalidateQueries({queryKey:["configuration-history"]})]) });
}

/* ── Products ── */
export interface ProductFilters {
  q?: string;
  type?: string;
  brand?: string;
  approvalStatus?: string;
  /** Server-side supplier scope — a PO line must belong to the PO's supplier
   *  (poService rejects a mismatch with 400 supplier_mismatch), and the catalog
   *  is far too large to filter a single page in the browser. */
  supplierId?: string;
  color?: string;
  size?: string;
  /** Selling price, integer minor units — never cost: see the API schema. */
  minPrice?: number;
  maxPrice?: number;
  minStock?: number;
  maxStock?: number;
  /** Epoch ms, already snapped to the user's local start/end of day. */
  createdFrom?: number;
  createdTo?: number;
  sort?: string;
  dir?: "asc" | "desc";
  page: number;
  pageSize: number;
}
export function useProducts(params: ProductFilters) {
  return useQuery({
    queryKey: ["products", params],
    queryFn: ({ signal }) =>
      apiFetch<Paginated<ProductSummaryDto>>("/products", {
        query: {
          q: params.q,
          type: params.type,
          brand: params.brand,
          approvalStatus: params.approvalStatus,
          supplierId: params.supplierId,
          color: params.color,
          size: params.size,
          minPrice: params.minPrice,
          maxPrice: params.maxPrice,
          minStock: params.minStock,
          maxStock: params.maxStock,
          createdFrom: params.createdFrom,
          createdTo: params.createdTo,
          sort: params.sort,
          dir: params.dir,
          page: params.page,
          pageSize: params.pageSize,
        },
        signal,
      }),
    placeholderData: (previousData) => previousData,
  });
}

export function useProductFilterOptions() {
  return useQuery({
    queryKey: ["products", "filter-options"],
    staleTime: 5 * 60_000,
    queryFn: () =>
      apiFetch<{
        data: { types: string[]; brands: string[]; suppliers: { id: string; name: string }[] };
      }>("/products/options").then((r) => r.data),
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: ["product", id],
    enabled: !!id,
    retry: (count, err) => {
      if (err instanceof ApiClientError && (err.status === 404 || err.code === "not_found")) {
        return false;
      }
      return count < 1;
    },
    queryFn: () => apiFetch<{ data: ProductDetailDto }>(`/products/${id}`).then((r) => r.data),
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductCreateInput) =>
      apiFetch<{ data: ProductDetailDto }>("/products", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductUpdateInput) =>
      apiFetch<{ data: ProductDetailDto }>(`/products/${id}`, { method: "PUT", body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      void qc.invalidateQueries({ queryKey: ["product", id] });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/products/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      void qc.invalidateQueries({ queryKey: ["product"] });
    },
  });
}

export function useProductPrices(productId: string | undefined) {
  return useQuery({
    queryKey: ["product-prices", productId],
    enabled: !!productId,
    queryFn: () =>
      apiFetch<{ data: ProductPriceDto[] }>(`/products/${productId}/prices`).then((r) => r.data),
  });
}

export function useSetPrice(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetPriceInput) =>
      apiFetch<{ data: unknown }>(`/products/${productId}/prices`, { method: "POST", body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["product-prices", productId] });
      void qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

/* ── Quotations ── */
export function useQuotationCustomerOptions(){return useQuery({queryKey:["customers","quotation-filter-options"],queryFn:({signal})=>fetchAllCustomers({sort:"companyName"},signal),staleTime:60_000});}
export function useQuotations(params: Partial<import("@hisense/shared").QuotationListInput>, enabled = true) {
 return useQuery({queryKey:["quotations",params],enabled,queryFn:({signal})=>apiFetch<Paginated<QuotationSummaryDto>>("/quotations",{query:params,signal}),placeholderData:previous=>previous});
}
export function useQuotation(id:string|undefined,archived=false){return useQuery({queryKey:["quotation",id,archived],enabled:!!id&&id!=="new",queryFn:()=>apiFetch<{data:QuotationDetailDto}>(`/quotations/${id}`,{query:{archived:archived?"true":undefined}}).then(r=>r.data)});}
export function useCreateQuotation(){const qc=useQueryClient();return useMutation({mutationFn:(input:QuotationCreateInput)=>apiFetch<{data:QuotationDetailDto}>("/quotations",{method:"POST",body:input}),onSuccess:r=>{qc.setQueryData(["quotation",r.data.id,false],r.data);void qc.invalidateQueries({queryKey:["quotations"]});}});}
export function useUpdateQuotation(id:string){const qc=useQueryClient();return useMutation({mutationFn:(input:QuotationUpdateInput)=>apiFetch<{data:QuotationDetailDto}>(`/quotations/${id}`,{method:"PUT",body:input}),onSuccess:r=>{qc.setQueryData(["quotation",id,false],r.data);void qc.invalidateQueries({queryKey:["quotations"]});}});}
export function useQuotationAction(id:string){const qc=useQueryClient();return useMutation({mutationFn:(input:{action:"publish"|"revise";expectedUpdatedAt:number})=>apiFetch<{data:QuotationDetailDto}>(`/quotations/${id}/${input.action}`,{method:"POST",body:{expectedUpdatedAt:input.expectedUpdatedAt}}),onSuccess:r=>{qc.setQueryData(["quotation",id,false],r.data);void qc.invalidateQueries({queryKey:["quotations"]});}});}
export function useDeleteQuotation(){const qc=useQueryClient();return useMutation({mutationFn:(input:{id:string;expectedUpdatedAt:number})=>apiFetch<void>(`/quotations/${input.id}`,{method:"DELETE",query:{expectedUpdatedAt:input.expectedUpdatedAt}}),onSuccess:()=>{void qc.invalidateQueries({queryKey:["quotations"]});void qc.invalidateQueries({queryKey:["quotation"]});}});}

/* ── Suppliers ── */
/** `classification`: bộ lọc theo đội của màn Nhà cung cấp (29/09/2026); "unclassified" = chưa chọn đội. */
export function useSuppliers(params: { q?: string; page?: number; pageSize?: number; classification?: "vietnam" | "china" | "unclassified" }, enabled = true) {
  return useQuery({
    queryKey: ["suppliers", params],
    // Tài khoản cổng NCC không được đọc danh sách NCC; trang Cổng tắt hook này cho họ.
    enabled,
    queryFn: ({ signal }) =>
      apiFetch<Paginated<SupplierDto>>("/suppliers", {
        query: {
          q: params.q,
          page: params.page,
          // Pickers omit pageSize and still need the full vendor list; the
          // list page passes an explicit size. Cap is 200 in paginationSchema.
          pageSize: params.pageSize ?? 100,
          classification: params.classification,
        },
        signal,
      }),
    placeholderData: (previousData) => previousData,
  });
}

export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: ["supplier", id],
    enabled: !!id,
    retry: (count, err) => {
      if (err instanceof ApiClientError && (err.status === 404 || err.code === "not_found")) {
        return false;
      }
      return count < 1;
    },
    queryFn: () => apiFetch<{ data: SupplierDetailDto }>(`/suppliers/${id}`).then((r) => r.data),
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierCreateInput) =>
      apiFetch<{ data: SupplierDetailDto }>("/suppliers", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["suppliers"] }),
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierUpdateInput) =>
      apiFetch<{ data: SupplierDetailDto }>(`/suppliers/${id}`, { method: "PUT", body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suppliers"] });
      void qc.invalidateQueries({ queryKey: ["supplier", id] });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/suppliers/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suppliers"] });
      void qc.invalidateQueries({ queryKey: ["supplier"] });
    },
  });
}

export function useProformaInvoices(params: {invoiceAvailable?:"true"|"false";keepPiId?:string;q?:string;page?:number;pageSize?:number;status?:string;customerId?:string;respBy?:string;from?:number;to?:number;sort?:string}, enabled = true) {
  return useQuery({queryKey:["pis",params],enabled,queryFn:({signal})=>apiFetch<Paginated<PiSummaryDto>>("/proforma-invoices",{query:params,signal}),placeholderData:previous=>previous});
}

export function useProformaInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ["pi", id],
    enabled: !!id && id !== "new",
    queryFn: () => apiFetch<{ data: PiDetailDto }>(`/proforma-invoices/${id}`).then((r) => r.data),
  });
}

function piInvalidate(qc: ReturnType<typeof useQueryClient>, id?: string) {
  void qc.invalidateQueries({ queryKey: ["pis"] });
  void qc.invalidateQueries({ queryKey: ["dashboard"] });
  if (id) void qc.invalidateQueries({ queryKey: ["pi", id] });
  // Ghi hoặc hủy một khoản thu là đổi cách chia tiền xuống từng hóa đơn.
  if (id) void qc.invalidateQueries({ queryKey: ["receivable", id] });
}
export function useCreatePI() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProformaInvoiceCreateInput) => apiFetch<{ data: PiDetailDto }>("/proforma-invoices", { method: "POST", body: input }),
    onSuccess: () => piInvalidate(qc),
  });
}
export function useUpdatePI(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProformaInvoiceUpdateInput) => apiFetch<{ data: PiDetailDto }>(`/proforma-invoices/${id}`, { method: "PUT", body: input }),
    onSuccess: () => piInvalidate(qc, id),
  });
}
export function useDeletePI() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiFetch<void>(`/proforma-invoices/${id}`, { method: "DELETE" }), onSuccess: () => piInvalidate(qc) });
}
export function useAddPayment(piId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PiPaymentInput) => apiFetch<{ data: PiDetailDto }>(`/proforma-invoices/${piId}/payments`, { method: "POST", body: input }),
    onSuccess: () => piInvalidate(qc, piId),
  });
}
export function useDeletePayment(piId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({pid,reason}: {pid:string;reason:string}) => apiFetch<{ data: PiDetailDto }>(`/proforma-invoices/${piId}/payments/${pid}`, { method: "DELETE", body:{reason} }),
    onSuccess: () => piInvalidate(qc, piId),
  });
}

/* ── Invoices ── */
export function useInvoices(params: { q?: string; page?: number; pageSize?: number; customerId?:string;respBy?:string;from?:number;to?:number;sort?:string;archived?:"true"|"false" }, enabled = true) {
  return useQuery({
    queryKey: ["invoices", params],
    enabled,
    queryFn: ({ signal }) =>
      apiFetch<Paginated<InvoiceSummaryDto>>("/invoices", {
        query: params,
        signal,
      }),
    placeholderData: (previousData) => previousData,
  });
}
export function useInvoice(id: string | undefined, archived=false) {
  return useQuery({ queryKey: ["invoice", id,archived], enabled: !!id && id !== "new", queryFn: () => apiFetch<{ data: InvoiceDetailDto }>(`/invoices/${id}`,{query:{archived:archived?"true":undefined}}).then((r) => r.data) });
}
/** Một dòng PI kèm phần còn xuất hóa đơn được — nguồn của bảng chọn số lượng. */
export interface InvoiceableLine {
  piItemId: string; productCode: string; subRef: string | null; description: string | null;
  unit: string | null; unitPrice: number; ordered: number; invoiced: number; remaining: number;
}
/** Còn xuất được bao nhiêu trên từng dòng của PI. Không đệm: trần đổi sau mỗi lần lập. */
export function useInvoiceableLines(piId: string | undefined) {
  return useQuery({
    queryKey: ["invoiceable", piId],
    enabled: !!piId,
    staleTime: 0,
    queryFn: ({ signal }) => apiFetch<{ data: InvoiceableLine[] }>(`/invoices/remaining/${piId}`, { signal }),
  });
}

/** Số dư từng hóa đơn của một PI sau khi chia tiền đã thu. */
export interface PiReceivable {
  invoices: { invoiceId: string; invNo: string; date: number | null; total: number; paid: number; outstanding: number }[];
  allocations: { paymentId: string; invoiceId: string; amount: number }[];
  unapplied: number;
}
export function useReceivableForPi(piId: string | undefined) {
  return useQuery({
    queryKey: ["receivable", piId],
    enabled: !!piId,
    staleTime: 0,
    queryFn: ({ signal }) => apiFetch<{ data: PiReceivable }>(`/invoices/receivable/${piId}`, { signal }),
  });
}

export function useCreateInvoiceFromPi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: string|{piId:string;expectedPiUpdatedAt?:number;requestId?:string;lines?:{piItemId:string;quantity:number}[]}) => apiFetch<{ data: InvoiceDetailDto }>("/invoices", { method: "POST", body: typeof input==="string"?{piId:input}:input }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["invoices"] }); void qc.invalidateQueries({ queryKey: ["pis"] }); void qc.invalidateQueries({queryKey:["pi"]}); void qc.invalidateQueries({queryKey:["invoiceable"]}); void qc.invalidateQueries({queryKey:["receivable"]}); },
  });
}

/** PATCH, not PUT (routes/invoices.ts). Re-pointing `piId` flips hasInvoice on
 *  BOTH the old and the new PI, so the PI list has to be invalidated too. */
export function useUpdateInvoice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvoiceUpdateInput) => apiFetch<{ data: InvoiceDetailDto }>(`/invoices/${id}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["invoice", id] });
      void qc.invalidateQueries({ queryKey: ["pis"] });
      void qc.invalidateQueries({queryKey:["pi"]});
    },
  });
}
export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/invoices/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["pis"] });
      void qc.invalidateQueries({queryKey:["pi"]});
    },
  });
}

/* ── Purchase Orders ── */
export function usePurchaseOrders(params: {
  q?: string; page?: number; pageSize?: number; status?: string; sort?: string;
  supplierId?: string; priority?: string; from?: number; to?: number;
}, enabled = true) {
  return useQuery({
    queryKey: ["pos", params],
    enabled,
    queryFn: ({ signal }) =>
      apiFetch<
        Paginated<import("@hisense/shared").PurchaseOrderSummaryDto> & {
          statusCounts: { status: string; count: number }[];
        }
      >("/purchase-orders", {
        query: {
          q: params.q, page: params.page, pageSize: params.pageSize ?? 100,
          status: params.status, sort: params.sort, supplierId: params.supplierId, priority: params.priority, from: params.from, to: params.to,
        },
        signal,
      }),
    placeholderData: (previousData) => previousData,
  });
}
export function usePurchaseOrder(id: string | undefined) {
  return useQuery({ queryKey: ["po", id], enabled: !!id && id !== "new", queryFn: () => apiFetch<{ data: PurchaseOrderDetailDto }>(`/purchase-orders/${id}`).then((r) => r.data) });
}
export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PurchaseOrderCreateInput) => apiFetch<{ data: PurchaseOrderDetailDto }>("/purchase-orders", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pos"] }),
  });
}
export function useUpdatePurchaseOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PurchaseOrderUpdateInput) => apiFetch<{ data: PurchaseOrderDetailDto }>(`/purchase-orders/${id}`, { method: "PUT", body: input }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["pos"] }); void qc.invalidateQueries({ queryKey: ["po", id] }); },
  });
}
export function useDeletePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/purchase-orders/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pos"] }),
  });
}
export function useCreatePoFromPi() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: import("@hisense/shared").PoFromPiInput) => apiFetch<{ data: import("@hisense/shared").PurchaseOrderSummaryDto[] }>("/purchase-orders/from-pi", { method: "POST", body: input }), onSuccess: () => qc.invalidateQueries({ queryKey: ["pos"] }) });
}
export function useSetPoStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (status: string) => apiFetch<{ data: unknown }>(`/purchase-orders/${id}/status`, { method: "PUT", body: { status } }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["pos"] }); void qc.invalidateQueries({ queryKey: ["po", id] }); } });
}

/* ── Packing Lists ── */
export function usePackingLists(params: { q?: string; page?: number; pageSize?: number;piId?:string;respBy?:string;from?:number;to?:number;sort?:string }) {
  return useQuery({
    queryKey: ["pls", params],
    queryFn: ({ signal }) =>
      apiFetch<Paginated<import("@hisense/shared").PackingListSummaryDto>>("/packing-lists", {
        query: params,
        signal,
      }),
    placeholderData: (previousData) => previousData,
  });
}
export function usePackingList(id: string | undefined) {
  // `/packing-lists/new` is the create route — skip the fetch, like useSampleOrder.
  return useQuery({ queryKey: ["pl", id], enabled: !!id && id !== "new", queryFn: () => apiFetch<{ data: PackingListDetailDto }>(`/packing-lists/${id}`).then((r) => r.data) });
}
export function useCreatePackingList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PackingListCreateInput) => apiFetch<{ data: PackingListDetailDto }>("/packing-lists", { method: "POST", body: input }),
    onSuccess: () => {void qc.invalidateQueries({ queryKey: ["pls"] });void qc.invalidateQueries({queryKey:["packing-availability"]});void qc.invalidateQueries({queryKey:["pi"]});},
  });
}
export function useUpdatePackingList(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PackingListUpdateInput) => apiFetch<{ data: PackingListDetailDto }>(`/packing-lists/${id}`, { method: "PUT", body: input }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["pls"] });void qc.invalidateQueries({queryKey:["packing-availability"]});void qc.invalidateQueries({queryKey:["pi"]}); void qc.invalidateQueries({ queryKey: ["pl", id] }); },
  });
}
export function useDeletePackingList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/packing-lists/${id}`, { method: "DELETE" }),
    onSuccess: () => {void qc.invalidateQueries({ queryKey: ["pls"] });void qc.invalidateQueries({queryKey:["packing-availability"]});void qc.invalidateQueries({queryKey:["pi"]});},
  });
}
export function useCreatePackingFromPi() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (piId: string) => apiFetch<{ data: import("@hisense/shared").PackingListDetailDto }>("/packing-lists/from-pi", { method: "POST", body: { piId } }), onSuccess: () => {void qc.invalidateQueries({ queryKey: ["pls"] });void qc.invalidateQueries({queryKey:["packing-availability"]});void qc.invalidateQueries({queryKey:["pi"]});} });
}

/* ── Delivery Notes ── */
export function useDeliveryNotes(params: { q?: string; page?: number; pageSize?: number;status?:import("@hisense/shared").DeliveryStatus;customerId?:string;refType?:"packing"|"invoice";from?:number;to?:number;sort?:string;archived?:"true"|"false" }) {
  return useQuery({
    queryKey: ["dns", params],
    queryFn: ({ signal }) =>
      apiFetch<Paginated<import("@hisense/shared").DeliveryNoteSummaryDto>>("/delivery-notes", {
        query:params,
        signal,
      }),
    placeholderData: (previousData) => previousData,
  });
}
export function useDeliveryNote(id: string | undefined,archived=false) {
  return useQuery({ queryKey: ["dn", id,archived], enabled: !!id && id !== "new", queryFn: () => apiFetch<{ data: DeliveryNoteDetailDto }>(`/delivery-notes/${id}`,{query:{archived:archived?"true":undefined}}).then((r) => r.data) });
}
/** Also the status quick-action — deliveryStatus rides on the same PUT. */
export function useUpdateDeliveryNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DeliveryNoteUpdateInput) => apiFetch<{ data: DeliveryNoteDetailDto }>(`/delivery-notes/${id}`, { method: "PUT", body: input }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["dns"] });void qc.invalidateQueries({queryKey:["delivery-source"]}); void qc.invalidateQueries({ queryKey: ["dn", id] }); },
  });
}
export function useDeleteDeliveryNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/delivery-notes/${id}`, { method: "DELETE" }),
    onSuccess: () => {void qc.invalidateQueries({ queryKey: ["dns"] });void qc.invalidateQueries({queryKey:["delivery-source"]});void qc.invalidateQueries({queryKey:["invoice"]});void qc.invalidateQueries({queryKey:["pl"]});void qc.invalidateQueries({queryKey:["delivery-source"]});},
  });
}
export function useCreateDnFromPacking() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (plId: string) => apiFetch<{ data: import("@hisense/shared").DeliveryNoteDetailDto }>("/delivery-notes/from-packing", { method: "POST", body: { plId } }), onSuccess: () => {void qc.invalidateQueries({ queryKey: ["dns"] });void qc.invalidateQueries({queryKey:["delivery-source"]});void qc.invalidateQueries({queryKey:["invoice"]});void qc.invalidateQueries({queryKey:["pl"]});void qc.invalidateQueries({queryKey:["delivery-source"]});} });
}
export function useCreateDnFromInvoice() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (invoiceId: string) => apiFetch<{ data: import("@hisense/shared").DeliveryNoteDetailDto }>("/delivery-notes/from-invoice", { method: "POST", body: { invoiceId } }), onSuccess: () => {void qc.invalidateQueries({ queryKey: ["dns"] });void qc.invalidateQueries({queryKey:["delivery-source"]});void qc.invalidateQueries({queryKey:["invoice"]});void qc.invalidateQueries({queryKey:["pl"]});void qc.invalidateQueries({queryKey:["delivery-source"]});} });
}

/* ── Samples ── */
export function useSampleOrders(params: {
  q?: string;
  page?: number;
  pageSize?: number;
  /** Lọc ở MÁY CHỦ — lọc mảng đã tải chỉ lọc được đúng trang đang xem. */
  status?: string; orderType?:string; partyId?:string; team?:string; from?:number; to?:number; sort?:string;
}) {
  return useQuery({
    queryKey: ["sos", params],
    queryFn: ({ signal }) =>
      apiFetch<
        Paginated<import("@hisense/shared").SampleOrderSummaryDto> & {
          statusCounts: { status: string; count: number }[];
        }
      >("/sample-orders", {
        query: {
          q: params.q,
          status: params.status, orderType:params.orderType, partyId:params.partyId, team:params.team, from:params.from, to:params.to, sort:params.sort,
          page: params.page,
          pageSize: params.pageSize ?? 100,
        },
        signal,
      }),
    placeholderData: (previousData) => previousData,
  });
}
export function useSampleOrder(id: string | undefined) {
  return useQuery({ queryKey: ["so", id], enabled: !!id && id !== "new", queryFn: () => apiFetch<{ data: import("@hisense/shared").SampleOrderDetailDto }>(`/sample-orders/${id}`).then((r) => r.data) });
}
export function useCreateSampleOrder() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: import("@hisense/shared").SampleOrderCreateInput) => apiFetch<{ data: import("@hisense/shared").SampleOrderDetailDto }>("/sample-orders", { method: "POST", body: input }), onSuccess: () => qc.invalidateQueries({ queryKey: ["sos"] }) });
}
export function useUpdateSampleOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: import("@hisense/shared").SampleOrderUpdateInput) => apiFetch<{ data: import("@hisense/shared").SampleOrderDetailDto }>(`/sample-orders/${id}`, { method: "PUT", body: input }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["sos"] }); void qc.invalidateQueries({ queryKey: ["so", id] }); } });
}
/** The ONLY way to change status: neither the create nor the update schema
 *  carries the field, and create always starts at Draft. */
export function useSetSampleOrderStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (status: import("@hisense/shared").SampleStatus) => apiFetch<{ data: import("@hisense/shared").SampleOrderDetailDto }>(`/sample-orders/${id}/status`, { method: "PUT", body: { status } }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["sos"] }); void qc.invalidateQueries({ queryKey: ["so", id] }); } });
}
/**
 * "Add to DB" — đẩy các dòng gõ tay của đơn mẫu lên danh mục sản phẩm.
 * Làm mới cả danh sách sản phẩm lẫn chính đơn, vì dòng vừa được nối productId.
 */
export function useAddSampleOrderToCatalog(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (previewToken?:string) => apiFetch<{ data: import("@hisense/shared").SampleAddToCatalogResult }>(`/sample-orders/${id}/add-to-catalog`, { method: "POST", body:{previewToken} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      void qc.invalidateQueries({ queryKey: ["so", id] });
    },
  });
}
export function useDeleteSampleOrder() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiFetch<void>(`/sample-orders/${id}`, { method: "DELETE" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["sos"] }) });
}
export function useSampleCards(params: { q?: string; page?: number; pageSize?: number; customerId?:string; type?:string; brand?:string; from?:number; to?:number; sort?:string }) {
  return useQuery({
    queryKey: ["scs", params],
    queryFn: ({ signal }) =>
      apiFetch<Paginated<import("@hisense/shared").SampleCardDto>>("/sample-cards", {
        query: { q: params.q, customerId:params.customerId,type:params.type,brand:params.brand,from:params.from,to:params.to,sort:params.sort, page: params.page, pageSize: params.pageSize ?? 100 },
        signal,
      }),
    placeholderData: (previousData) => previousData,
  });
}
export function useSampleCard(id: string | undefined) {
  return useQuery({ queryKey: ["sc", id], enabled: !!id && id !== "new", queryFn: () => apiFetch<{ data: import("@hisense/shared").SampleCardDto }>(`/sample-cards/${id}`).then((r) => r.data) });
}
export function useCreateSampleCard() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: import("@hisense/shared").SampleCardCreateInput) => apiFetch<{ data: import("@hisense/shared").SampleCardDto }>("/sample-cards", { method: "POST", body: input }), onSuccess: () => qc.invalidateQueries({ queryKey: ["scs"] }) });
}
export function useUpdateSampleCard(id: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: import("@hisense/shared").SampleCardUpdateInput) => apiFetch<{ data: import("@hisense/shared").SampleCardDto }>(`/sample-cards/${id}`, { method: "PUT", body: input }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["scs"] }); void qc.invalidateQueries({ queryKey: ["sc", id] }); } });
}
export function useDeleteSampleCard() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => apiFetch<void>(`/sample-cards/${id}`, { method: "DELETE" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["scs"] }) });
}

/* ── Insight ── */
/** Kỳ báo cáo, epoch ms. Bỏ trống → máy chủ dùng 12 tháng gần nhất. */
export interface InsightRangeParams {
  from?: number;
  to?: number;
  /** Chỉ một đội (29/09/2026) — cho tài khoản thấy cả hai khu vực. */
  region?: "vietnam" | "china";
}
export function useAnalytics(range: InsightRangeParams = {}) {
  return useQuery({
    queryKey: ["analytics", range],
    queryFn: ({ signal }) =>
      apiFetch<{ data: import("@hisense/shared").AnalyticsDto }>("/analytics/sales", {
        query: { from: range.from, to: range.to, region: range.region },
        signal,
      }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });
}
export function useProfitReport(params:Partial<import("@hisense/shared").ProfitQueryInput>&{adminOnlyScope?:string}={},enabled=true){return useQuery({queryKey:["profit",params],enabled,queryFn:({signal})=>apiFetch<{data:import("@hisense/shared").ProfitReportDto}>("/analytics/profit",{query:params,signal}).then(r=>r.data)});}

export function useProductFiles(productId: string, enabled = true) {
  return useQuery({queryKey:["product-files",productId],enabled:enabled&&!!productId,queryFn:({signal})=>apiFetch<{data:{id:string;fileName:string;contentType:string|null;sizeBytes:number;status:string;createdAt:number}[]}>(`/product-files/products/${productId}`,{signal}).then(r=>r.data)});
}
/**
 * Tải tệp lên theo NHIỀU PHẦN.
 *
 * Trình duyệt cắt tệp theo cỡ phần máy chủ báo về, gửi tuần tự từng phần rồi
 * bảo máy chủ ghép. Tuần tự chứ không song song: một tệp CAD 500MB chạy song
 * song sẽ chiếm hết băng thông của người dùng và dễ đứt giữa chừng hơn.
 */
export function useProductFileUpload(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({file,onProgress}:{file:File;onProgress?:(done:number,total:number)=>void}) => {
      const opened = await apiFetch<{data:{id:string;partSize:number;parts:number}}>("/product-files/uploads",{method:"POST",body:{productId,fileName:file.name,contentType:file.type||undefined,size:file.size}});
      const {id,partSize,parts} = opened.data;
      const uploaded: {partNumber:number;etag:string}[] = [];
      try {
        for (let i = 0; i < parts; i++) {
          const chunk = file.slice(i*partSize, Math.min((i+1)*partSize, file.size));
          const res = await apiFetch<{data:{partNumber:number;etag:string}}>(`/product-files/uploads/${id}/parts/${i+1}`,{method:"PUT",rawBody:chunk});
          uploaded.push(res.data);
          onProgress?.(i+1, parts);
        }
        await apiFetch(`/product-files/uploads/${id}/complete`,{method:"POST",body:{parts:uploaded}});
      } catch (e) {
        // Bỏ dở thì dọn ngay, đừng để lại bản ghi treo và phần đã lên trong kho.
        await apiFetch(`/product-files/uploads/${id}/abort`,{method:"POST"}).catch(()=>{});
        throw e;
      }
      return id;
    },
    onSuccess: () => { void qc.invalidateQueries({queryKey:["product-files",productId]}); },
  });
}
export function useDeleteProductFile(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/product-files/${id}`,{method:"DELETE"}),
    onSuccess: () => { void qc.invalidateQueries({queryKey:["product-files",productId]}); },
  });
}
export function useMailAccounts() {
  return useQuery({queryKey:["mail-accounts"],queryFn:({signal})=>apiFetch<{data:{id:string;address:string;label:string|null}[]}>("/mail/accounts",{signal}).then(r=>r.data)});
}
export function useSendMail() {
  return useMutation({mutationFn:(body:{from:string;to:string;subject:string;body:string;documentNo:string;kind:string})=>apiFetch("/mail/send",{method:"POST",body})});
}
export function usePriceSuggestion(productId: string, params: {customerId?: string; quantity?: number; unit?: string}, enabled = true) {
  return useQuery({
    queryKey:["price-suggestion",productId,params],
    enabled: enabled && !!productId,
    queryFn:({signal})=>apiFetch<{data:import("@hisense/shared").SellingPriceResult&{quantityInBase:number|null;converted:boolean;productUnit?:string|null}}>(`/products/${productId}/price-suggestion`,{query:{customerId:params.customerId,quantity:params.quantity,unit:params.unit},signal}).then(r=>r.data),
  });
}
export function usePriceTiers(productId: string) {
  return useQuery({queryKey:["price-tiers",productId],enabled:!!productId,queryFn:({signal})=>apiFetch<{data:{id:string;fromQuantity:number;price:number;currency:string}[]}>(`/products/${productId}/price-tiers`,{signal}).then(r=>r.data)});
}
export function useSetPriceTiers(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {tiers:{fromQuantity:number;price:number}[]}) => apiFetch(`/products/${productId}/price-tiers`,{method:"PUT",body}),
    onSuccess: () => { void qc.invalidateQueries({queryKey:["price-tiers",productId]}); },
  });
}
export function usePiStatus(params: Partial<import("@hisense/shared").ProfitQueryInput> = {}) {
  return useQuery({queryKey:["pi-status",params],queryFn:({signal})=>apiFetch<{data:{rows:import("@hisense/shared").PiStatusRow[];period:{from:number;to:number};generatedAt:number}}>("/analytics/pi-status",{query:params,signal}).then(r=>r.data)});
}
export interface ProductProfitReport {groups:import("@hisense/shared").ProductProfitGroup[];period:{from:number;to:number};generatedAt:number;invoicedOnly:boolean;}
export function useProductProfit(params:Partial<import("@hisense/shared").ProfitQueryInput>&{invoicedOnly?:boolean}={},enabled=true){const {invoicedOnly,...rest}=params;return useQuery({queryKey:["profit-by-product",params],enabled,queryFn:({signal})=>apiFetch<{data:ProductProfitReport}>("/analytics/profit/by-product",{query:{...rest,...(invoicedOnly?{invoicedOnly:"1"}:{})},signal}).then(r=>r.data)});}
/* ── Companies (payment entities) ── */
type CompanyDetail = import("@hisense/shared").CompanyDetailDto;
type CompanyUpsert = import("@hisense/shared").CompanyUpsertInput;

/** Chi tiết kèm cột ngân hàng — máy chủ gác settings:manage, hook chỉ gọi khi cần. */
/** Bộ đếm số chứng từ — chỉ đọc, chỉ vai trò quản trị gọi được. */
export function useDocumentCounters(enabled = true) {
  return useQuery({
    queryKey: ["settings", "counters"],
    enabled,
    queryFn: () =>
      apiFetch<{ data: { key: string; nextValue: number; prefix: string | null; pad:number|null; updatedAt: number }[] }>(
        "/settings/counters",
      ).then((r) => r.data),
  });
}

export function useCompanyDetails(enabled = true) {
  return useQuery({
    queryKey: ["companies", "detail"],
    enabled,
    queryFn: () => apiFetch<{ data: CompanyDetail[] }>("/companies/detail").then((r) => r.data),
  });
}
export function useSaveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: CompanyUpsert }) =>
      apiFetch<{ data: CompanyDetail }>(id ? `/companies/${id}` : "/companies", {
        method: id ? "PUT" : "POST",
        body,
      }).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["companies"] });
      void qc.invalidateQueries({queryKey:["configuration-history"]});
    },
  });
}

/* ── Deleted records (restore) ── */
export interface DeletedRecordsParams {
  q?: string;
  table?: string;
  page: number;
  pageSize: number;
}
export function useDeletedRecords(params: DeletedRecordsParams) {
  return useQuery({
    queryKey: ["deleted", params],
    queryFn: ({ signal }) =>
      apiFetch<Paginated<import("@hisense/shared").DeletedRecordDto>>("/deleted-records", {
        query: { q: params.q, table: params.table, page: params.page, pageSize: params.pageSize },
        signal,
      }),
    placeholderData: (prev) => prev,
  });
}
/** Các bảng thực sự có trong kho lưu trữ — nguồn cho ô lọc theo loại. */
export function useDeletedTables() {
  return useQuery({
    queryKey: ["deleted", "tables"],
    staleTime: 60_000,
    queryFn: () => apiFetch<{ data: { table: string; count: number }[] }>("/deleted-records/tables").then((r) => r.data),
  });
}
/** Bản lưu đầy đủ của một bản ghi — máy chủ che giá vốn theo quyền. */
export function useDeletedSnapshot(id: string | null) {
  return useQuery({
    queryKey: ["deleted", "snapshot", id],
    enabled: !!id,
    queryFn: () => apiFetch<{ data: { snapshot: unknown; redacted: boolean; originalTable: string } }>(
      `/deleted-records/${id}/snapshot`).then((r) => r.data),
  });
}
/**
 * Khôi phục một bản ghi đã xoá. `identity` chỉ dành cho sản phẩm: khôi phục với
 * mã / Sub Ref mới khi cặp cũ đã có sản phẩm khác dùng (khách chốt 19/10/2026).
 */
export function useRestoreRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, identity }: { id: string; identity?: import("@hisense/shared").DeletedRecordRestoreInput }) =>
      apiFetch<{ data: unknown }>(`/deleted-records/${id}/restore`, { method: "POST", body: identity }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

/* ── Statements ── */
export function useStatementReport(params:Partial<import("@hisense/shared").StatementQueryInput>,enabled=true){return useQuery({queryKey:["statements","report",params],enabled,queryFn:({signal})=>apiFetch<import("@hisense/shared").StatementReportDto>("/statements",{query:params,signal})});}
export function useStatements(params: {
  customerId?: string;
  from?: number;
  to?: number;
  /** true = chỉ chứng từ còn nợ. Mặc định trả CẢ đã thu đủ, vì đó mới là bảng
   *  kê đối chiếu gửi khách. */
  outstandingOnly?: boolean;
}) {
  return useQuery({
    queryKey: ["statements", params],
    queryFn: ({ signal }) =>
      apiFetch<{ data: StatementDto[] }>("/statements", {
        query: {
          customerId: params.customerId,
          from: params.from,
          to: params.to,
          outstandingOnly: params.outstandingOnly ? "1" : undefined,
        },
        signal,
      }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });
}

/** `enabled` lets callers skip the fetch for roles without users:manage (403). */
export function useUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["users"],
    enabled: options?.enabled ?? true,
    queryFn: () => apiFetch<{ data: UserRow[] }>("/users").then((r) => r.data),
  });
}

/**
 * id + display name of ACTIVE users, for "assigned to" pickers.
 *
 * GET /users needs users:manage (admin only), so a manager creating a customer
 * could never populate the customer-service field. /users/options is gated on
 * records:read instead and exposes no email/role/status.
 */
/** `forRegion`: chỉ người thấy được khách của khu vực đó — ô chọn người theo đơn (29/09/2026). */
/**
 * `editorsOf`: chỉ người được lập / sửa ở menu đó (ô Người phụ trách của PI, yêu cầu v1.8 mục 4.2). Danh sách này đổi khi admin
 * thêm người, khoá tài khoản hay đổi ô tick, nên giữ trong bộ nhớ đệm ngắn hơn danh sách người theo đơn.
 */
export function useUserOptions(forRegion?: "vietnam" | "china", opts: { editorsOf?: "proformaInvoices" } = {}) {
  return useQuery({
    queryKey: ["user-options", forRegion ?? "all", opts.editorsOf ?? "any"],
    staleTime: opts.editorsOf ? 30_000 : 5 * 60_000,
    queryFn: () =>
      apiFetch<{ data: { id: string; displayName: string }[] }>("/users/options", {
        query: { forRegion, editorsOf: opts.editorsOf },
      }).then((r) => r.data),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserCreateInput) =>
      apiFetch<{ data: unknown }>("/users", { method: "POST", body: input }),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["users"] }),qc.invalidateQueries({queryKey:["user-options"]}),qc.invalidateQueries({queryKey:["user-security"]})]),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UserUpdateInput & { id: string }) =>
      apiFetch<{ data: UserRow }>(`/users/${id}`, { method: "PUT", body: input }).then((r) => r.data),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["users"] }),qc.invalidateQueries({queryKey:["user-options"]}),qc.invalidateQueries({queryKey:["user-security"]})]),
  });
}

/** Admin password reset — 204, nothing about the password comes back. */
export function useResetUserPassword() {
  const qc=useQueryClient();
  return useMutation({
    onSuccess:()=>{void qc.invalidateQueries({queryKey:["users"]});void qc.invalidateQueries({queryKey:["user-security"]});},
    mutationFn: ({ id, ...input }: UserPasswordResetInput & { id: string }) =>
      apiFetch<void>(`/users/${id}/password`, { method: "POST", body: input }),
  });
}

export type UserRow = import('@hisense/shared').UserManagementDto;
/** Bảng quyền của một tài khoản (chỉ admin đọc được). */
export interface UserAccessDto {
  mode: "role" | "custom";
  customerScope: "all" | "assigned";
  canViewCost: boolean;
  /** Khu vực dữ liệu (29/09/2026) — áp cho cả hai chế độ. */
  region: import("@hisense/shared").DataRegion;
  grants: Record<string, import("@hisense/shared").AccessVerb[]>;
  customerIds: string[];
  /** Nhà cung cấp RIÊNG TƯ người này được thấy (0097) — cùng bảng với ô Người theo dõi trên hồ sơ nhà cung cấp. */
  supplierIds: string[];
  preset: Record<string, import("@hisense/shared").AccessVerb[]>;
}

/** P-Process — đơn đang ở công đoạn nào. */
export function usePProcess() {
  return useQuery({
    queryKey: ["p-process"],
    queryFn: () => apiFetch<{ data: { rows: import("@hisense/shared").PProcessRow[]; unlinked: { orders: number; purchaseOrders: number }; generatedAt: number } }>("/p-process").then((r) => r.data),
  });
}

/** Quy trình thường — đơn không chạy quy trình P đang ở bước nào (29/10/2026). */
export function useOrderProcess() {
  return useQuery({
    queryKey: ["order-process"],
    queryFn: () => apiFetch<{ data: { rows: import("@hisense/shared").OrderProcessRow[]; truncated: boolean; unlinked: { orders: number; purchaseOrders: number }; generatedAt: number } }>("/order-process").then((r) => r.data),
  });
}

export function useMarkPProcess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ remove, ...body }: import("@hisense/shared").PProcessMarkInput & { remove?: boolean }) =>
      apiFetch<{ data: { ok: boolean } }>(`/p-process/marks${remove ? "/remove" : ""}`, { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["p-process"] }),
  });
}

export function useUserAccess(id: string, enabled = true) {
  return useQuery({
    queryKey: ["user-access", id],
    enabled,
    queryFn: () => apiFetch<{ data: UserAccessDto }>(`/users/${id}/access`).then((r) => r.data),
  });
}

export function useSetUserAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Omit<UserAccessDto, "preset">) =>
      apiFetch<{ data: UserAccessDto }>(`/users/${id}/access`, { method: "PUT", body }).then((r) => r.data),
    onSuccess: (_d, v) => Promise.all([
      qc.invalidateQueries({ queryKey: ["user-access", v.id] }),
      qc.invalidateQueries({ queryKey: ["users"] }),
      // Danh sách khách theo đơn của người này là ô Người theo đơn trên hồ sơ khách; khu vực đổi thì ô chọn người theo đơn đổi.
      qc.invalidateQueries({ queryKey: ["customers"] }),
      qc.invalidateQueries({ queryKey: ["customer"] }),
      qc.invalidateQueries({ queryKey: ["user-options"] }),
      // Người được thấy nhà cung cấp riêng tư là ô Người theo dõi trên hồ sơ nhà cung cấp (28/10/2026).
      qc.invalidateQueries({ queryKey: ["suppliers"] }),
      qc.invalidateQueries({ queryKey: ["supplier"] }),
    ]),
  });
}

export function useUserSecurityEvents(id:string) {
 return useQuery({queryKey:['user-security',id],queryFn:()=>apiFetch<{data:import('@hisense/shared').UserSecurityEventDto[]}>(`/users/${id}/security-events`).then(r=>r.data)});
}


export function useRestoreInvoice(){const qc=useQueryClient();return useMutation({mutationFn:(id:string)=>apiFetch<{data:InvoiceDetailDto}>(`/invoices/${id}/restore`,{method:"POST"}),onSuccess:()=>{for(const key of ["invoices","invoice","pis","pi"])void qc.invalidateQueries({queryKey:[key]});}});}

export function usePackingAvailability(piId:string|undefined,excludeId?:string){return useQuery({queryKey:["packing-availability",piId,excludeId],enabled:!!piId,queryFn:()=>apiFetch<{data:import("@hisense/shared").PackingAvailabilityDto}>(`/packing-lists/availability/${piId}`,{query:{excludeId}}).then(r=>r.data)});}

export function useDeliverySource(refType:"packing"|"invoice",sourceId:string|undefined,excludeId?:string){return useQuery({queryKey:["delivery-source",refType,sourceId,excludeId],enabled:!!sourceId,queryFn:()=>apiFetch<{data:import("@hisense/shared").DeliverySourceDto}>(`/delivery-notes/source/${refType}/${sourceId}`,{query:{excludeId}}).then(r=>r.data)});}
export function useCreateDeliveryShipment(){const qc=useQueryClient();return useMutation({mutationFn:(input:import("@hisense/shared").DeliveryShipmentInput)=>apiFetch<{data:DeliveryNoteDetailDto}>("/delivery-notes",{method:"POST",body:input}),onSuccess:()=>{for(const key of ["dns","dn","pl","invoice","delivery-source"])void qc.invalidateQueries({queryKey:[key]});}});}

export function useConfigurationHistory(resource:'settings'|'company',id?:string,enabled=true){return useQuery({queryKey:['configuration-history',resource,id],enabled:enabled&&(resource==='settings'||!!id),queryFn:()=>apiFetch<{data:import('@hisense/shared').ConfigurationEventDto[]}>(resource==='settings'?'/settings/history':`/companies/${id}/history`).then(r=>r.data)});}

/* ─────────────  Cổng nhà cung cấp: quản lý ngay trên hồ sơ NCC  ───────────── */

export function useSupplierPortal(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["supplier-portal", id],
    enabled: !!id && enabled,
    queryFn: () => apiFetch<{ data: import("@hisense/shared").SupplierPortalDto }>(`/suppliers/${id}/portal`).then((r) => r.data),
  });
}

function useSupplierPortalMutation<TInput>(id: string, method: "POST" | "PUT", suffix = "") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<{ data: import("@hisense/shared").SupplierPortalDto }>(`/suppliers/${id}/portal${suffix}`, { method, body: input }).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["supplier-portal", id] });
      // Mã đăng nhập nằm trên hồ sơ NCC; tài khoản cổng nằm trong danh sách người dùng.
      void qc.invalidateQueries({ queryKey: ["supplier", id] });
      void qc.invalidateQueries({ queryKey: ["suppliers"] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export const useOpenSupplierPortal = (id: string) =>
  useSupplierPortalMutation<import("@hisense/shared").SupplierPortalOpenInput>(id, "POST");
export const useUpdateSupplierPortal = (id: string) =>
  useSupplierPortalMutation<import("@hisense/shared").SupplierPortalUpdateInput>(id, "PUT");
export const useResetSupplierPortalPassword = (id: string) =>
  useSupplierPortalMutation<import("@hisense/shared").UserPasswordResetInput>(id, "POST", "/password");
